import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import HeroSection from './components/HeroSection';
import ConclusionCard from './components/ConclusionCard';
import WhyCalledDangerous from './components/WhyCalledDangerous';
import FxRisks from './components/FxRisks';
import DmmFxSafety from './components/DmmFxSafety';
import BeginnerSafetyTips from './components/BeginnerSafetyTips';
import NoNeedToWorry from './components/NoNeedToWorry';
import FaqSection from './components/FaqSection';
import SafetySummary from './components/SafetySummary';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createFAQStructuredData, createBreadcrumbStructuredData } from '../../utils/structuredData';

export default function Safety() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sho-navi.com';

  // SEO設定
  usePageSEO({
    title: 'DMM FXは危険？安全性を徹底解説｜金融庁登録・全額信託保全',
    description: 'DMM FXの安全性を徹底解説。金融庁登録業者で全額信託保全を完備。DMMグループの信頼性、セキュリティ対策、リスク管理体制など、初心者が知っておくべき安全性のポイントを詳しく説明します。',
    keywords: 'DMM FX, 安全性, 危険, 信託保全, 金融庁, セキュリティ, リスク管理',
    ogImage: 'https://public.readdy.ai/ai/img_res/6eb29d4c71b1569b29bd98f16547bddb.jpg',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXは危険？安全性を徹底解説',
        description: 'DMM FXの安全性について詳しく解説。金融庁登録、全額信託保全、DMMグループの信頼性、セキュリティ対策など、初心者が安心して取引できる理由を説明します。',
        image: 'https://readdy.ai/api/search-image?query=DMM%20FX%20safety&width=1200&height=630&seq=safety-article&orientation=landscape',
        author: 'DMM FX情報局',
        datePublished: '2024-01-15',
        dateModified: new Date().toISOString().split('T')[0],
        url: `${siteUrl}/safety`
      }),
      createFAQStructuredData([
        {
          question: 'DMM FXは金融庁に登録されていますか?',
          answer: 'はい、DMM FXは金融庁に正式に登録された第一種金融商品取引業者です（関東財務局長（金商）第1629号）。金融商品取引法に基づく厳格な規制を受けており、安心して取引できます。'
        },
        {
          question: 'DMM FXが倒産したら預けたお金はどうなりますか?',
          answer: 'DMM FXは全額信託保全を完備しています。お客様の資産は信託銀行に分別管理されており、万が一DMM FXが倒産しても、預けた資金は全額保護されます。'
        },
        {
          question: 'DMM FXのセキュリティ対策は万全ですか?',
          answer: 'はい、DMM FXは128bit SSL暗号化通信、二段階認証、生体認証ログインなど、最新のセキュリティ技術を導入しています。また、24時間365日のシステム監視体制も整えています。'
        },
        {
          question: 'FX取引自体にリスクはありますか?',
          answer: 'はい、FX取引にはレバレッジによる損失拡大リスク、為替変動リスク、流動性リスクなどがあります。しかし、DMM FXでは強制ロスカット機能やリスク管理ツールを提供しており、適切なリスク管理が可能です。'
        },
        {
          question: '初心者でも安全に取引できますか?',
          answer: 'はい、DMM FXは初心者向けのサポート体制が充実しています。24時間サポート、デモ口座での練習、分かりやすい取引ツールなど、初心者が安全に取引を始められる環境が整っています。'
        }
      ], `${siteUrl}/safety`),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '安全性', url: '/safety' }
      ], siteUrl)
    ]
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX初心者ガイド', url: '/fx-guide' },
            { name: '安全性', url: '/safety' }
          ]} />
        </div>
      </div>

      <HeroSection />

      {/* 目次 */}
      <section className="py-6 md:py-10 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-100">
            <h2 className="text-xs md:text-sm font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
              <i className="ri-list-ordered text-teal-600 mr-2 w-4 h-4 flex items-center justify-center"></i>
              この記事の内容
            </h2>
            <nav>
              <ol className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
                {[
                  { label: '結論：DMM FXは安全です', href: '#conclusion' },
                  { label: 'なぜ「危険」と言われるのか？', href: '#why-dangerous' },
                  { label: 'FX自体に存在するリスク', href: '#fx-risks' },
                  { label: 'DMM FXの安全性が高い理由', href: '#dmm-safety' },
                  { label: '初心者が安全に始める6つのポイント', href: '#beginner-tips' },
                  { label: '過度な心配はいりません', href: '#no-worry' },
                  { label: 'よくある質問', href: '#faq' },
                  { label: 'まとめ：安心して始められる理由', href: '#summary' },
                ].map((item, i) => (
                  <li key={i}>
                    <a
                      href={item.href}
                      className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer flex items-center"
                    >
                      <span className="text-teal-500 font-semibold mr-2 w-5 text-right">
                        {i + 1}.
                      </span>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>
      </section>

      <div id="conclusion">
        <ConclusionCard />
      </div>
      <div id="why-dangerous">
        <WhyCalledDangerous />
      </div>
      <div id="fx-risks">
        <FxRisks />
      </div>
      <div id="dmm-safety">
        <DmmFxSafety />
      </div>
      <div id="beginner-tips">
        <BeginnerSafetyTips />
      </div>

      {/* 利用者の声へのリンク */}
      <section className="py-6 md:py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4 md:p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-xs md:text-base text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      <div id="no-worry">
        <NoNeedToWorry />
      </div>
      <div id="faq">
        <FaqSection />
      </div>
      <div id="summary">
        <SafetySummary />
      </div>

      {/* 関連記事 */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            関連記事
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-6">
            <a
              href="/disadvantages"
              className="group p-4 md:p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                <i className="ri-alert-line text-lg md:text-2xl text-orange-600"></i>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                  DMM FXのデメリット
                </h3>
              </div>
              <p className="text-xs md:text-sm text-gray-600 hidden md:block">
                デメリットを理解してリスク管理
              </p>
            </a>

            <a
              href="/leverage"
              className="group p-4 md:p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                <i className="ri-line-chart-line text-lg md:text-2xl text-purple-600"></i>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                  レバレッジとは？
                </h3>
              </div>
              <p className="text-xs md:text-sm text-gray-600 hidden md:block">
                レバレッジの仕組みとリスク管理
              </p>
            </a>

            <a
              href="/support"
              className="group p-4 md:p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                <i className="ri-customer-service-2-line text-lg md:text-2xl text-teal-600"></i>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  サポート体制
                </h3>
              </div>
              <p className="text-xs md:text-sm text-gray-600 hidden md:block">
                安心のサポート体制を確認
              </p>
            </a>

            <a
              href="/beginner-guide"
              className="group p-4 md:p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                <i className="ri-book-open-line text-lg md:text-2xl text-emerald-600"></i>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">
                  初心者ガイド
                </h3>
              </div>
              <p className="text-xs md:text-sm text-gray-600 hidden md:block">
                初心者でも安心して始められる
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* 口座開設手順へのリンク */}
      <section className="py-6 md:py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 md:p-6 border-l-4 border-emerald-500 shadow-md">
            <p className="text-xs md:text-base text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20financial%20security%20concept%20with%20soft%20teal%20and%20green%20gradient%20background%20showing%20shield%20protection%20symbols%20and%20upward%20trending%20chart%20lines%20modern%20minimalist%20clean%20design%20professional%20atmosphere&width=1440&height=400&seq=safety-cta-1&orientation=landscape"
            alt="DMM FX 安心して始めよう"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-700/90 via-emerald-700/85 to-teal-700/90"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center bg-white/15 backdrop-blur-sm text-white text-xs md:text-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-4 md:mb-6">
            <i className="ri-shield-check-line mr-2 w-4 h-4 flex items-center justify-center"></i>
            金融庁登録・全額信託保全・24時間サポート
          </div>

          <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            安心・安全のDMM FXで<br />
            FX取引を始めよう
          </h2>

          <p className="text-sm md:text-lg text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto">
            今なら新規口座開設＋取引で<strong>最大50万円キャッシュバック</strong>
            キャンペーン実施中！
          </p>

          <a
            href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
            rel="nofollow"
            className="inline-block bg-white text-teal-700 px-8 md:px-10 py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
          >
            無料で口座開設する
          </a>

          {/* Tracking pixel – hidden from UI */}
          <img
            border={0}
            width={1}
            height={1}
            src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82"
            alt=""
            style={{ display: 'none' }}
          />

          <p className="text-xs md:text-sm text-white/80 mt-4 md:mt-6">
            口座開設は最短5分 | 口座維持費無料 | デモ取引で無料練習可能
          </p>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
